import { FamilyUnit } from '@zorro/clients';
import Decimal from 'decimal.js';

function validateParams<T, K extends keyof T>(params: T, requiredParams: K[]) {
  for (const param of requiredParams) {
    if (params[param] === undefined) {
      throw new Error(`${String(param)} is required for this calculation`);
    }
  }
}

export const DEFAULT_CHILD_COUNT = 1.6;

export type AllowanceParams = {
  employeeAllowance?: number;
  spouseAllowance?: number;
  childrenAllowance?: number;
  familyAllowance?: number;
  childrenAvgPremium?: number;
  employeeAvgPremium?: number;
  avgChildCount?: number;
};

export type ConvertorFunction = (params: AllowanceParams) => number;

export type AllowanceConversion = {
  [K in FamilyUnit]: ConvertorFunction;
};

export const allowance4TierUSDToPercentageConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance', 'employeeAvgPremium']);
    return new Decimal(values.employeeAllowance ?? 0)
      .dividedBy(values.employeeAvgPremium || 1)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['spouseAllowance', 'employeeAvgPremium']);
    const dividedBy = new Decimal(values.employeeAvgPremium ?? 0)
      .mul(2)
      .toNumber();
    return new Decimal(values.spouseAllowance ?? 0)
      .dividedBy(dividedBy || 1)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, [
      'childrenAllowance',
      'employeeAvgPremium',
      'childrenAvgPremium',
    ]);
    const dividedBy = new Decimal(values.employeeAvgPremium ?? 0)
      .add(values.childrenAvgPremium ?? 0)
      .toNumber();
    return new Decimal(values.childrenAllowance ?? 0)
      .dividedBy(dividedBy || 1)
      .toNumber();
  },
  [FamilyUnit.FAMILY]: (values: AllowanceParams) => {
    validateParams(values, [
      'familyAllowance',
      'employeeAvgPremium',
      'childrenAvgPremium',
    ]);
    const dividedBy = new Decimal(values.employeeAvgPremium ?? 0)
      .add(values.childrenAvgPremium ?? 0)
      .mul(2)
      .toNumber();
    return new Decimal(values.familyAllowance ?? 0)
      .dividedBy(dividedBy || 1)
      .toNumber();
  },
};

export const allowance4TierPercentageToUSDConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance', 'employeeAvgPremium']);
    return new Decimal(values.employeeAllowance ?? 0)
      .mul(values.employeeAvgPremium ?? 0)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['spouseAllowance', 'employeeAvgPremium']);
    return new Decimal(values.spouseAllowance ?? 0)
      .mul(values.employeeAvgPremium ?? 0)
      .mul(2)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, [
      'childrenAllowance',
      'employeeAvgPremium',
      'childrenAvgPremium',
    ]);
    const added = new Decimal(values.employeeAvgPremium ?? 0)
      .add(values.childrenAvgPremium ?? 0)
      .toNumber();
    return new Decimal(values.childrenAllowance ?? 0).mul(added).toNumber();
  },
  [FamilyUnit.FAMILY]: (values: AllowanceParams) => {
    validateParams(values, [
      'familyAllowance',
      'employeeAvgPremium',
      'childrenAvgPremium',
    ]);
    const added = new Decimal(values.employeeAvgPremium ?? 0)
      .add(values.childrenAvgPremium ?? 0)
      .toNumber();
    return new Decimal(values.familyAllowance ?? 0)
      .mul(added)
      .mul(2)
      .toNumber();
  },
};

export const allowance4TierTo3TierPercentageConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance']);
    return values.employeeAllowance ?? 0;
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['spouseAllowance', 'employeeAllowance']);
    const calculation = new Decimal(values.spouseAllowance ?? 0)
      .mul(2)
      .sub(values.employeeAllowance ?? 0)
      .toNumber();
    return Math.max(calculation, 0);
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, [
      'childrenAllowance',
      'employeeAllowance',
      'employeeAvgPremium',
      'childrenAvgPremium',
    ]);
    const added = new Decimal(values.employeeAvgPremium ?? 0)
      .add(values.childrenAvgPremium ?? 0)
      .toNumber();
    const multiplier = new Decimal(values.childrenAllowance ?? 0)
      .mul(added)
      .toNumber();
    const employeeMultiplier = new Decimal(values.employeeAllowance ?? 0)
      .mul(values.employeeAvgPremium ?? 0)
      .toNumber();
    const subtracted = new Decimal(multiplier)
      .sub(employeeMultiplier)
      .toNumber();
    const dividedBy = new Decimal(subtracted)
      .dividedBy(values.childrenAvgPremium || 1)
      .toNumber();

    return Math.max(dividedBy, 0);
  },
  [FamilyUnit.FAMILY]: () => {
    return 0;
  },
};

export const allowance3TierTo4TierPercentageConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance']);
    return values.employeeAllowance ?? 0;
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['spouseAllowance', 'employeeAllowance']);
    return new Decimal(values.spouseAllowance ?? 0)
      .add(values.employeeAllowance ?? 0)
      .dividedBy(2)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, [
      'childrenAllowance',
      'employeeAllowance',
      'employeeAvgPremium',
      'childrenAvgPremium',
    ]);
    const childrenMultiplier = new Decimal(values.childrenAllowance ?? 0).mul(
      values.childrenAvgPremium ?? 0
    );
    const employeeMultiplier = new Decimal(values.employeeAllowance ?? 0).mul(
      values.employeeAvgPremium ?? 0
    );
    const added = new Decimal(childrenMultiplier ?? 0)
      .add(employeeMultiplier ?? 0)
      .toNumber();
    const denominator = new Decimal(values.employeeAvgPremium ?? 0)
      .add(values.childrenAvgPremium ?? 0)
      .toNumber();

    return new Decimal(added).dividedBy(denominator || 1).toNumber();
  },
  [FamilyUnit.FAMILY]: (values: AllowanceParams) => {
    validateParams(values, [
      'childrenAllowance',
      'employeeAllowance',
      'spouseAllowance',
      'employeeAvgPremium',
      'childrenAvgPremium',
    ]);
    const childrenMultiplier = new Decimal(values.childrenAllowance ?? 0)
      .mul(values.childrenAvgPremium ?? 0)
      .toNumber();
    const employeePlusSpouse = new Decimal(values.employeeAllowance ?? 0)
      .add(values.spouseAllowance ?? 0)
      .mul(values.employeeAvgPremium ?? 0)
      .toNumber();
    const added = new Decimal(childrenMultiplier)
      .add(employeePlusSpouse)
      .toNumber();
    const denominator = new Decimal(values.employeeAvgPremium ?? 0)
      .mul(2)
      .add(values.childrenAvgPremium ?? 0)
      .toNumber();

    return new Decimal(added).dividedBy(denominator || 1).toNumber();
  },
};

export const allowance3TierPercentageToUSDConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance', 'employeeAvgPremium']);
    return new Decimal(values.employeeAllowance ?? 0)
      .mul(values.employeeAvgPremium ?? 0)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['spouseAllowance', 'employeeAvgPremium']);
    return new Decimal(values.spouseAllowance ?? 0)
      .mul(values.employeeAvgPremium ?? 0)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, ['childrenAllowance', 'childrenAvgPremium']);
    return new Decimal(values.childrenAllowance ?? 0)
      .mul(values.childrenAvgPremium ?? 0)
      .dividedBy(values.avgChildCount ?? DEFAULT_CHILD_COUNT)
      .toNumber();
  },
  [FamilyUnit.FAMILY]: (values: AllowanceParams) => {
    validateParams(values, ['familyAllowance']);
    return values.familyAllowance ?? 0;
  },
};

export const allowance3TierUSDToPercentageConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance', 'employeeAvgPremium']);
    return new Decimal(values.employeeAllowance ?? 0)
      .dividedBy(values.employeeAvgPremium || 1)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['spouseAllowance', 'employeeAvgPremium']);
    return new Decimal(values.spouseAllowance ?? 0)
      .dividedBy(values.employeeAvgPremium || 1)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, ['childrenAllowance', 'childrenAvgPremium']);
    return new Decimal(values.childrenAllowance ?? 0)
      .mul(values.avgChildCount ?? DEFAULT_CHILD_COUNT)
      .dividedBy(values.childrenAvgPremium || 1)
      .toNumber();
  },
  [FamilyUnit.FAMILY]: (values: AllowanceParams) => {
    validateParams(values, ['familyAllowance']);
    return values.familyAllowance ?? 0;
  },
};

export const allowance3TierTo4TierDollarConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance']);
    return new Decimal(values.employeeAllowance ?? 0).toNumber();
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance', 'spouseAllowance']);
    return new Decimal(values.employeeAllowance ?? 0)
      .add(values.spouseAllowance ?? 0)
      .toNumber();
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance', 'childrenAllowance']);
    const multiplier = new Decimal(values.childrenAllowance ?? 0).mul(
      values.avgChildCount ?? DEFAULT_CHILD_COUNT
    );
    return new Decimal(values.employeeAllowance ?? 0)
      .add(multiplier)
      .toNumber();
  },
  [FamilyUnit.FAMILY]: (values: AllowanceParams) => {
    validateParams(values, [
      'employeeAllowance',
      'spouseAllowance',
      'childrenAllowance',
    ]);
    const multiplier = new Decimal(values.childrenAllowance ?? 0).mul(
      values.avgChildCount ?? DEFAULT_CHILD_COUNT
    );
    return new Decimal(values.employeeAllowance ?? 0)
      .add(values.spouseAllowance ?? 0)
      .add(multiplier)
      .toNumber();
  },
};

export const allowance4TierTo3TierDollarConvertor: AllowanceConversion = {
  [FamilyUnit.EMPLOYEE_ONLY]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance']);
    return new Decimal(values.employeeAllowance ?? 0).toNumber();
  },
  [FamilyUnit.EMPLOYEE_SPOUSE]: (values: AllowanceParams) => {
    validateParams(values, ['spouseAllowance', 'employeeAllowance']);
    const subtracted = new Decimal(values.spouseAllowance ?? 0)
      .sub(values.employeeAllowance ?? 0)
      .toNumber();
    return Math.max(subtracted, 0);
  },
  [FamilyUnit.EMPLOYEE_CHILD]: (values: AllowanceParams) => {
    validateParams(values, ['employeeAllowance', 'childrenAllowance']);
    const subtracted = new Decimal(values.childrenAllowance ?? 0)
      .sub(values.employeeAllowance ?? 0)
      .toNumber();
    const numerator = Math.max(subtracted, 0);
    return new Decimal(numerator)
      .dividedBy(values.avgChildCount ?? DEFAULT_CHILD_COUNT)
      .toNumber();
  },
  [FamilyUnit.FAMILY]: (values: AllowanceParams) => {
    validateParams(values, ['familyAllowance']);
    return values.familyAllowance ?? 0;
  },
};
